import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCheck, Nfc } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LogoMark } from "@/components/Logo";
import { SectionHead } from "@/components/SectionHead";

export const Simulator = () => {
  const { t, isAr } = useLang();
  const s = t.sim;
  const [mode, setMode] = useState("arrival");
  const [phase, setPhase] = useState("idle");
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const tap = () => {
    if (phase === "tapping") return;
    clearTimeout(timer.current);
    setPhase("tapping");
    timer.current = setTimeout(() => setPhase("sent"), 1100);
  };

  const pickMode = (m) => {
    clearTimeout(timer.current);
    setMode(m);
    setPhase("idle");
  };

  const msg = mode === "arrival" ? s.notif.arrive : s.notif.depart;
  const time = mode === "arrival" ? s.notif.timeA : s.notif.timeD;

  return (
    <section id="demo" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead kicker={s.kicker} title={s.title} sub={s.sub} center />

        <div className="mt-12 grid items-center gap-10 rounded-[2.5rem] border border-cardline bg-white p-6 sm:p-10 lg:grid-cols-2">
          <div>
            <div className="flex flex-wrap justify-center gap-3">
              {[["arrival", s.arrival], ["departure", s.departure]].map(([m, label]) => (
                <button
                  key={m}
                  data-testid={`sim-mode-${m}`}
                  onClick={() => pickMode(m)}
                  className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                    mode === m ? "bg-pine text-cream" : "border border-line bg-cream text-inksoft hover:border-pine"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center">
              <div className="w-60 rounded-[2.2rem] border-4 border-pine-dark bg-pine p-4 shadow-[0_32px_60px_-30px_rgba(94,33,196,0.5)]" data-testid="sim-terminal">
                <div className="grid min-h-[104px] place-items-center rounded-xl bg-cream px-3 py-4 text-center">
                  {phase === "idle" && (
                    <div>
                      <Nfc className="mx-auto h-7 w-7 text-pine" />
                      <p className="mt-2 text-xs font-bold text-inksoft">{s.screenIdle}</p>
                    </div>
                  )}
                  {phase === "tapping" && (
                    <motion.p animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 0.9, repeat: Infinity }} className="text-xs font-bold text-terra">
                      {s.screenRead}
                    </motion.p>
                  )}
                  {phase === "sent" && (
                    <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}>
                      <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-pine text-white">
                        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </span>
                      <p className="mt-2 text-xs font-bold text-pine">{s.screenDone} · {time}</p>
                    </motion.div>
                  )}
                </div>
                <div className="mx-auto mt-3 h-9 w-12 rounded-full bg-sun shadow-inner" />
                <div className="mt-3 flex justify-center gap-1.5 pb-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="h-1.5 w-1.5 rounded-full bg-cream/40" />
                  ))}
                </div>
              </div>

              <motion.div
                animate={{ y: phase === "idle" ? 0 : -44, rotate: phase === "idle" ? 0 : isAr ? 5 : -5, scale: phase === "idle" ? 1 : 0.96 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="-mt-5 flex w-56 cursor-pointer items-center gap-3 rounded-2xl border border-cardline bg-cream px-4 py-3 shadow-lg"
                onClick={tap}
                data-testid="sim-card"
              >
                <svg viewBox="0 0 48 48" className="h-9 w-9 shrink-0" fill="none" aria-hidden="true">
                  <rect x="3" y="10" width="42" height="28" rx="8" fill="#FBF6F1" stroke="#ECE0D8" />
                  <circle cx="14" cy="24" r="3" fill="#7938EA" />
                  <path d="M20 18c3.4 3.4 3.4 8.6 0 12" stroke="#7938EA" strokeWidth="2.4" strokeLinecap="round" />
                  <path d="M26 14.5c5.6 5.6 5.6 13.5 0 19" stroke="#FF6131" strokeWidth="2.4" strokeLinecap="round" />
                </svg>
                <span>
                  <span className="block text-[11px] font-bold uppercase tracking-widest text-inkmute">itminane</span>
                  <span className="block font-display text-sm font-bold text-ink">{s.notif.chat}</span>
                </span>
              </motion.div>

              <button
                data-testid="hero-tap-simulator-trigger-button"
                onClick={tap}
                disabled={phase === "tapping"}
                className="mt-7 rounded-full bg-pine px-8 py-3.5 font-bold text-cream transition-all hover:bg-pine-dark disabled:opacity-60"
              >
                {phase === "tapping" ? s.tapping : phase === "sent" ? s.tapAgain : s.tapBtn}
              </button>
            </div>
          </div>

          <div className="mx-auto w-full max-w-[350px]">
            <div className="overflow-hidden rounded-[2rem] border border-line bg-white shadow-[0_40px_80px_-40px_rgba(34,24,51,0.35)]" data-testid="sim-phone">
              <div className="flex items-center gap-3 bg-[#008069] px-4 py-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-cream">
                  <LogoMark className="h-7 w-7" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-white" data-testid="sim-notification-chat">{s.notif.chat}</span>
                  <span className="block text-[11px] text-white/70">{s.notif.online}</span>
                </span>
              </div>

              <div className="wa-chat-bg flex h-[300px] flex-col justify-end p-4">
                <AnimatePresence mode="wait">
                  {phase === "sent" ? (
                    <motion.div
                      key={`${mode}-${isAr}`}
                      initial={{ scale: 0.6, opacity: 0, y: 24 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ type: "spring", stiffness: 320, damping: 22 }}
                      className="wa-out max-w-[88%] self-end rounded-2xl rounded-tr-sm p-3 shadow"
                      data-testid="sim-notification"
                    >
                      <p className="text-[14px] leading-snug text-ink">{msg}</p>
                      <p className="mt-1 flex items-center justify-end gap-1.5 text-[11px] font-semibold text-inksoft">
                        {s.notif.meta} · {time}
                        <CheckCheck className="h-4 w-4 text-[#53BDEB]" />
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="rounded-xl border border-dashed border-line bg-white/70 p-4 text-center text-xs font-semibold text-inksoft">
                      {s.empty}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <p className="mt-4 text-center text-xs leading-relaxed text-inkmute">{s.hint}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
